import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getContact, patchContact } from '../../../api/contact';
import { hasAdminAccess } from "../../../utils/roleAccess";

/**
 * Inline "General Information" card for a contact — the same fields as
 * ContactProfilePage's Overview tab, extracted so it can be embedded directly
 * inside another page (e.g. a property's Owner tab) instead of forcing a full
 * navigation away. CG, 2026-08-06 (Monday 12709346275): agents should be able
 * to view/edit an owner's details "unified" within the property profile.
 *
 * Self-contained: fetches and edits the contact by id on its own, independent
 * of any parent page's state.
 */
export default function ContactGeneralInfoCard({ contactId }) {
  const { user } = useSelector((state) => state.auth);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getContact(contactId)
      .then((res) => {
        if (active) setContact(res.data);
      })
      .catch(() => {
        if (active) toast.error("Failed to load this contact's details");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [contactId]);

  const isAdmin = hasAdminAccess(user);
  const hasPortalAccount = !!(
    contact &&
    (contact.has_portal_account || contact.buyer_preferences?.portal_user_found)
  );
  const canEditDetails = isAdmin || !hasPortalAccount;

  const handleUpdateContact = async (data) => {
    try {
      setIsUpdating(true);
      const res = await patchContact(contactId, data);
      setContact(res.data);
      toast.success('Contact updated');
    } catch {
      toast.error('Failed to update contact');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 text-[#55C5D0] animate-spin" />
      </div>
    );
  }
  if (!contact) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-50 flex items-center justify-between">
          <h4 className="font-bold text-gray-900 text-sm">General Information</h4>
          {isUpdating && <Loader2 className="w-4 h-4 text-gray-300 animate-spin" />}
        </div>

        {!canEditDetails && (
          <div className="mx-4 sm:mx-5 mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-[13px] text-amber-800 font-medium leading-snug">
              This contact has a Buyer Portal account. Their details are read-only —
              only an admin can update them.
            </p>
          </div>
        )}

        <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-8">
          <InfoItem label="Full Name" value={contact.name} />
          {contact.preferred_name && <InfoItem label="Preferred Name" value={contact.preferred_name} />}
          <InfoItem label="Email Address" value={contact.email} isLink href={`mailto:${contact.email}`} />

          {canEditDetails ? (
            <>
              <InfoInput label="Mobile Phone" val={contact.phone} onSave={(v) => handleUpdateContact({ phone: v })} />
              <InfoInput
                label="Work Phone / Alternate"
                val={contact.alternate_phone}
                onSave={(v) => handleUpdateContact({ alternate_phone: v })}
              />
            </>
          ) : (
            <>
              <InfoItem label="Mobile Phone" value={contact.phone} isLink={!!contact.phone} href={`tel:${contact.phone}`} />
              <InfoItem
                label="Work Phone / Alternate"
                value={contact.alternate_phone}
                isLink={!!contact.alternate_phone}
                href={`tel:${contact.alternate_phone}`}
              />
            </>
          )}

          {canEditDetails ? (
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                Birthday
              </label>
              <input
                type="date"
                defaultValue={contact.birthday || ''}
                onBlur={(e) => handleUpdateContact({ birthday: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-sm transition-all"
              />
            </div>
          ) : (
            <InfoItem label="Birthday" value={contact.birthday} />
          )}

          {canEditDetails ? (
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                Contact Method
              </label>
              <select
                defaultValue={contact.preferred_contact_method || 'email'}
                onChange={(e) => handleUpdateContact({ preferred_contact_method: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-sm transition-all capitalize"
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          ) : (
            <InfoItem label="Contact Method" value={contact.preferred_contact_method || 'email'} className="capitalize" />
          )}

          <div className="md:col-span-2 flex flex-wrap gap-5 pt-3 border-t border-gray-50">
            <StatusToggle
              label="Unsubscribed"
              isActive={contact.do_not_contact}
              onToggle={() => handleUpdateContact({ do_not_contact: !contact.do_not_contact })}
              activeColor="bg-red-500"
              disabled={!canEditDetails}
            />
            <StatusToggle
              label="Email Marketing"
              isActive={contact.email_opt_in}
              onToggle={() => handleUpdateContact({ email_opt_in: !contact.email_opt_in })}
              disabled={!canEditDetails}
            />
            <StatusToggle
              label="SMS Marketing"
              isActive={contact.sms_opt_in}
              onToggle={() => handleUpdateContact({ sms_opt_in: !contact.sms_opt_in })}
              disabled={!canEditDetails}
            />
            <StatusToggle
              label="Buyer Advocate"
              isActive={contact.is_buyer_advocate}
              onToggle={() => handleUpdateContact({ is_buyer_advocate: !contact.is_buyer_advocate })}
              activeColor="bg-[#db2777]"
              disabled={!canEditDetails}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Contact Summary</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Status</span>
            <span className="font-bold text-[#55C5D0]">{contact.is_lead ? 'Active Lead' : 'Cold Contact'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Created</span>
            <span className="font-bold text-gray-700">{new Date(contact.created_at).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Assigned Agent</span>
            <span className="font-bold text-gray-700">{contact.agent_name || 'Unassigned'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const InfoItem = ({ label, value, isLink, href, className = '' }) => (
  <div className={className}>
    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">{label}</label>
    {isLink ? (
      <a href={href} className="text-sm font-bold text-[#55C5D0] hover:underline decoration-2 underline-offset-4">
        {value || 'Not provided'}
      </a>
    ) : (
      <p className="text-sm font-bold text-gray-900 leading-tight">{value || 'Not provided'}</p>
    )}
  </div>
);

const InfoInput = ({ label, val, onSave }) => (
  <div>
    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">{label}</label>
    <input
      type="text"
      defaultValue={val}
      onBlur={(e) => onSave(e.target.value)}
      placeholder={`Enter ${label}...`}
      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-sm transition-all"
    />
  </div>
);

const StatusToggle = ({ label, isActive, onToggle, activeColor = 'bg-[#10B981]', disabled = false }) => (
  <button
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    title={disabled ? 'Only admins can change a portal member’s details' : undefined}
    className={`flex items-center gap-2.5 py-1 px-1 group ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  >
    <div className={`w-9 h-[18px] rounded-full relative transition-all ${isActive ? activeColor : 'bg-gray-200'}`}>
      <div className={`absolute top-[3px] w-3 h-3 bg-white rounded-full transition-all ${isActive ? 'right-[3px]' : 'left-[3px]'}`}></div>
    </div>
    <span className={`text-xs font-bold text-gray-600 transition-colors ${disabled ? '' : 'group-hover:text-gray-900'}`}>{label}</span>
  </button>
);
